import { intl } from '@beelday/common'
import { useDispatch, useSelector } from 'app-redux'
import { TrainingRoomPhase } from 'common/types'
import { useQuestions, VotingModal } from 'features/questions'
import { useEffect, useMemo, useRef } from 'react'
import { MobileFrame } from 'room/in-person-mobile/mobile-frame'
import { MobileStage } from 'room/in-person-mobile/mobile-stage'
import { isAnswerable, stageFor } from 'room/in-person-mobile/model/stage'
import { Confirmation } from 'room/in-person-mobile/presentation/primitives'
import { setMiscPanelOpen } from 'room/common/redux'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import { selectFocusedFeature } from './redux'

/**
 * The participant's room on a phone or tablet (below 1024px), in person.
 *
 * The phone is for answering, not watching: what is shown at the front stays
 * on the projector and this screen rests until the trainer starts something
 * to answer (see `room/in-person-mobile/model/stage.ts`).
 *
 * A separate shell from `room-trainee-in-person.tsx` rather than a responsive
 * pass over it: the laptop room is the desktop stage minus video, while this
 * one replaces the stage, the circular menu and the misc drawer.
 */
export const RoomTraineeInPersonMobile = (): JSX.Element => {
	const dispatch = useDispatch()
	const roomAddress = useAssertedJoinedRoomAddress()
	const user = useAssertedWorkflowUser()
	const trainingRoomPhase = useSelector(state => state.room.trainingRoomPhase)
	const focusedFeature = useSelector(selectFocusedFeature)
	const questions = useQuestions()
	const stage = useMemo(() => stageFor(focusedFeature), [focusedFeature])

	// Someone reading the chat when the trainer starts a poll would otherwise
	// never see it: the panel covers the stage. Close it for anything that
	// asks for an answer - not on the first render, which is not a "start".
	const previousKey = useRef(stage.key)
	useEffect(() => {
		if (stage.key !== previousKey.current && isAnswerable(stage)) {
			dispatch(setMiscPanelOpen(false))
		}
		previousKey.current = stage.key
	}, [dispatch, stage])

	return (
		<MobileFrame live testId="in_person.mobile.room">
			{trainingRoomPhase === TrainingRoomPhase.INITIAL ? (
				<MobileStage stage={stage} />
			) : (
				// `SELECTING_GROUPS` and `REVIEWING_TASKS` belong to the real
				// `Grouping` feature, which a stationary scheme does not enable.
				<Confirmation
					title={
						<intl.Translate>in_person.room.unexpected_phase</intl.Translate>
					}
					icon={null}
				/>
			)}
			{questions.active && !focusedFeature ? (
				<VotingModal
					roomAddress={roomAddress}
					me={user.id}
					showResults={false}
				/>
			) : null}
		</MobileFrame>
	)
}

export default RoomTraineeInPersonMobile
